import { ProgressSpinner } from 'primereact/progressspinner';
import {
  CartItemInput,
  CartItemInterface,
  CategoriesWithPathsDocument,
  RootCategoriesDocument,
  ProductInterface,
  useAddProductsToCartMutation,
  ProductFieldsFragment,
  CategoryTree,
} from 'graphql/generated/magentoApi';
import { Category } from 'pages/CategoryTree';
import { Client, useClient } from 'urql';
import { DataView, DataViewPageParams } from 'primereact/dataview';
import {
  useParams,
  useSearchParams,
  useNavigate,
  To,
  NavigateOptions,
} from 'react-router-dom';
import { MenuItem } from 'primereact/menuitem';
// import { Sidebar } from 'primereact/sidebar';
import { TabView, TabPanel } from 'primereact/tabview';
import { useCartContext } from 'CartContext';
import {
  useGetProductsByCategoryPath,
  useGetProductsBySkus,
} from 'hooks/magentoHooks';
import { useMarketplace, useMarketplaceSiteDomain } from 'hooks/useMarketplace';
import { ReactElement, useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import CartItemList from 'components/CartItemList';
import { Head } from '../../components/head/Head';
import TokenProductCard from './TokenProductCard';
import './TokenCard.scss';

export const rootPath = 'shopping';

async function fetchCategories(
  client: Client,
  urlPaths: string[],
): Promise<Category[]> {
  if (urlPaths.length) {
    const result = await client
      .query(CategoriesWithPathsDocument, {
        urlPaths,
      })
      .toPromise();
    return result.data.categoryList;
  }
  const result = await client.query(RootCategoriesDocument).toPromise();
  return result.data.categoryList;
}

function buildCategoryPaths(
  pathParam?: string,
  categoriesParam?: string,
  root?: string,
): string[] {
  const paths: string[] = [];
  if (pathParam) {
    paths.push(pathParam);
  }
  if (categoriesParam) {
    paths.push(...categoriesParam.split(','));
  }

  if (!root) {
    return paths;
  }
  if (!paths.length) {
    paths.push(root);
  }
  return paths;
}

function buildSkusArray(skusParam: string | undefined): string[] {
  if (skusParam) {
    return [...skusParam.split(',')];
  }
  return [];
}

export default function TokenLanding(): ReactElement {
  const client = useClient();
  const navigateBase = useNavigate();
  const marketplace = useMarketplace();
  const marketplaceDomain = useMarketplaceSiteDomain();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = searchParams.get('page') || '1';
  const perPageParam = searchParams.get('per-page');
  //! temporarily set perPageParam to 300 due to a pagination bug
  const perPage = Number(perPageParam || 300);
  const openExternal = searchParams.get('open-external') === 'true';
  const categoriesParam = searchParams.get('categories') || undefined;
  const skusParam = searchParams.get('skus') || undefined;
  const { products: skuProducts } = useGetProductsBySkus({
    skus: buildSkusArray(skusParam),
  });
  const [currentPage, setCurrentPage] = useState<number>(Number(page) || 1);
  const [first, setFirst] = useState<number>(perPage * (currentPage - 1));
  const [, setBreadcrumbModel] = useState<MenuItem[]>([]);
  const [, setMenuModel] = useState<MenuItem[]>([
    { label: 'Shop Categories', items: [] },
  ]);
  const [, setLoadingCategories] = useState<boolean>(false);
  const [, setVisible] = useState<boolean>(false);
  // const [visibleRight, setVisibleRight] = useState(false);
  const [categoryPaths, setCategoryPaths] = useState(
    buildCategoryPaths(params['*'], categoriesParam, rootPath),
  );
  const {
    products,
    totalProducts,
    fetching: fetchingProducts,
  } = useGetProductsByCategoryPath({
    page: currentPage,
    categoryPaths,
    perPage,
    skuProducts,
  });
  const [product, setProduct] = useState<
    Array<ProductFieldsFragment | null> | undefined
  >([]);

  useEffect(() => {
    if (marketplace?.categories) {
      navigateBase(`/?categories=${marketplace?.categories}`);
    }
  }, [marketplace?.categories, navigateBase]);

  const [loading, setLoading] = useState(false);
  const [productSku, setCurrentProductSku] = useState<string>('');
  const [addProductState, addProducts] = useAddProductsToCartMutation();
  const {
    cartId,
    items,
    shipping,
    subTotal,
    total,
    taxes,
    updateItems,
    reset,
  } = useCartContext();

  const navigate = (to: To, options?: NavigateOptions) => {
    if (openExternal) {
      window.open(to as string, '_blank');
    } else {
      navigateBase(to, options);
    }
  };

  const navigateToPage = (pageNumber: number) => {
    if (pageNumber > 1) {
      searchParams.set('page', `${pageNumber}`);
    } else {
      searchParams.delete('page');
    }
    setSearchParams(searchParams);
  };
  useEffect(() => {
    const goToFirstPage =
      document.getElementsByClassName('p-paginator-first')[0];
    if (goToFirstPage) {
      goToFirstPage.classList.remove('p-disabled');
      goToFirstPage.removeAttribute('disabled');
      goToFirstPage.addEventListener('click', () => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
        navigateToPage(1);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  useEffect(() => {
    setFirst(perPage * (Number(page) - 1));
    setCurrentPage(Number(page));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    const items = addProductState.data?.addProductsToCart?.cart.items as
      | CartItemInterface[]
      | undefined;
    if (!addProductState.fetching && items?.length) {
      updateItems([...items]);
      setLoading(false);
      setVisible(true);
    } else if (addProductState.error) {
      setLoading(false);
      reset();
    }
  }, [addProductState, reset, updateItems]);

  useEffect(() => {
    setCategoryPaths(
      buildCategoryPaths(params['*'], categoriesParam, rootPath),
    );
  }, [categoriesParam, params]);

  useEffect(() => {
    setLoadingCategories(true);
    const paths = marketplace?.categories
      ? categoryPaths.filter(category => category !== rootPath)
      : [...categoryPaths];
    fetchCategories(client, paths).then(categories => {
      const firstCategory = categories[0] || {};

      const homeMenuItem = {
        label: 'Home',
        command: () => {
          const path =
            marketplace?.categories || marketplaceDomain?.categories
              ? `/category?categories=${
                  marketplace?.categories || marketplaceDomain?.categories
                }`
              : '/';
          navigateBase(path);
        },
      };

      const fetchedBreadcrumbs = (firstCategory.breadcrumbs?.map(item => {
        if (item.category_url_path === rootPath) {
          return homeMenuItem;
        }
        return {
          label: item.category_name,
          command: () => navigateBase(`/category/${item.category_url_path}`),
        };
      }) || []) as MenuItem[];

      const menuItems: MenuItem[] = [];
      if (!rootPath) {
        menuItems.push(homeMenuItem);
      }
      menuItems.push(...fetchedBreadcrumbs);
      if (firstCategory.url_path === rootPath) {
        menuItems.push(homeMenuItem);
      } else if (firstCategory.url_path) {
        menuItems.push({
          label: firstCategory.name,
          command: () => navigateBase(`/category/${firstCategory.url_path}`),
        });
      }

      setBreadcrumbModel(menuItems);

      const children = categories
        .reduce<Partial<CategoryTree>[]>((previous, category) => {
          category.children?.forEach(child => {
            if (!previous.find(c => c.url_path === child.url_path)) {
              previous.push(child);
            }
          });
          return previous;
        }, [])
        .filter(item => !!item.products?.total_count)
        .map(item => ({
          label: item.name,
          icon: 'pi pi-angle-right',
          command: () => navigateBase(`/category/${item.url_path}`),
        }));
      setMenuModel([{ label: 'Shop Categories', items: [...children] }]);
      setLoadingCategories(false);
    });
  }, [
    categoryPaths,
    client,
    marketplace?.categories,
    marketplaceDomain?.categories,
    navigateBase,
  ]);

  useEffect(() => {
    const inputSearch = document.getElementById('search-product');
    if (inputSearch) {
      // To fix style bug on small resolutions
      inputSearch.addEventListener('keypress', () => {
        setTimeout(() => {
          const list = document.querySelector(
            '.p-autocomplete-panel.p-component.p-connected-overlay-enter-done',
          ) as HTMLElement;
          const properties = inputSearch.getBoundingClientRect();
          if (list) {
            list.style.left = `${properties.left.toString()}px`;
            list.style.maxWidth = `${properties.width.toString()}px`;
          }
        }, 1000);
      });
    }
  }, []);
  const [updateCart, setUpdateCart] = useState(false);

  const onClickAddBtn = (sku: string) => {
    window.location.reload();
    if (openExternal) {
      navigate(`/store/cart?add-sku=${sku}`);
      return;
    }
    setLoading(true);
    setCurrentProductSku(sku);
    addProducts({
      cartId,
      cartItems: [
        {
          sku,
          quantity: 1,
        },
      ] as CartItemInput[],
    });
    ReactGA.event({
      category: 'ecommerce',
      action: 'add_to_cart',
      label: `Product Card Action ${sku}`,
    });
  };

  const itemTemplate = (product: ProductInterface) => (
    <TokenProductCard
      product={product}
      onClickAddBtn={onClickAddBtn}
      loading={loading && product.sku === productSku}
    />
  );

  const onPageChange = ({ page, first }: DataViewPageParams) => {
    setFirst(first);
    setCurrentPage(page + 1);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    navigateToPage(page + 1);
  };

  useEffect(() => {
    let filterProducts: any = [...products].filter(
      item => item?.vendor_company === 'CrowdPoint Technologies - crwdunit',
    ) as ProductFieldsFragment[];

    if (!fetchingProducts && !product?.length) {
      filterProducts = filterProducts.sort(
        (a, b) =>
          a.price_range.maximum_price?.final_price?.value -
          b.price_range.maximum_price?.final_price.value,
      );
      setProduct(filterProducts);
    }
  }, [fetchingProducts, product?.length, products]);
  const { t } = useTranslation();

  const tabHeaderTemplate = options => (
    <button
      type="button"
      onClick={options.onClick}
      className={`token-panel ${options.className}`}
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">
        <path d="M512 32C547.3 32 576 60.65 576 96V128H0V96C0 60.65 28.65 32 64 32H512zM576 416C576 451.3 547.3 480 512 480H64C28.65 480 0 451.3 0 416V224H576V416zM112 352C103.2 352 96 359.2 96 368C96 376.8 103.2 384 112 384H176C184.8 384 192 376.8 192 368C192 359.2 184.8 352 176 352H112zM240 384H368C376.8 384 384 376.8 384 368C384 359.2 376.8 352 368 352H240C231.2 352 224 359.2 224 368C224 376.8 231.2 384 240 384z" />
      </svg>
      {options.titleElement}
    </button>
  );

  const tabHeaderTemplateACH = options => (
    <button
      type="button"
      onClick={options.onClick}
      className={`token-panel ${options.className}`}
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
        <path d="M243.4 2.587C251.4-.8625 260.6-.8625 268.6 2.587L492.6 98.59C506.6 104.6 514.4 119.6 511.3 134.4C508.3 149.3 495.2 159.1 479.1 160V168C479.1 181.3 469.3 192 455.1 192H55.1C42.74 192 31.1 181.3 31.1 168V160C16.81 159.1 3.708 149.3 .6528 134.4C-2.402 119.6 5.429 104.6 19.39 98.59L243.4 2.587zM256 128C273.7 128 288 113.7 288 96C288 78.33 273.7 64 256 64C238.3 64 224 78.33 224 96C224 113.7 238.3 128 256 128zM127.1 416H167.1V224H231.1V416H280V224H344V416H384V224H448V420.3C448.6 420.6 449.2 420.1 449.8 421.4L497.8 453.4C509.5 461.2 514.7 475.8 510.6 489.3C506.5 502.8 494.1 512 480 512H31.1C17.9 512 5.458 502.8 1.372 489.3C-2.715 475.8 2.515 461.2 14.25 453.4L62.25 421.4C62.82 420.1 63.41 420.6 63.1 420.3V224H127.1V416z" />
      </svg>
      {options.titleElement}
    </button>
  );

  const tabHeaderTemplateWire = options => (
    <button
      type="button"
      onClick={options.onClick}
      className={`token-panel ${options.className}`}
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
        <path d="M535 7.03C544.4-2.343 559.6-2.343 568.1 7.029L632.1 71.02C637.5 75.52 640 81.63 640 87.99C640 94.36 637.5 100.5 632.1 104.1L568.1 168.1C559.6 178.3 544.4 178.3 535 168.1C525.7 159.6 525.7 144.4 535 135L558.1 111.1L384 111.1C370.7 111.1 360 101.2 360 87.99C360 74.74 370.7 63.99 384 63.99L558.1 63.1L535 40.97C525.7 31.6 525.7 16.4 535 7.03V7.03zM104.1 376.1L81.94 400L255.1 399.1C269.3 399.1 279.1 410.7 279.1 423.1C279.1 437.2 269.3 447.1 255.1 447.1L81.95 448L104.1 471C114.3 480.4 114.3 495.6 104.1 504.1C95.6 514.3 80.4 514.3 71.03 504.1L7.029 440.1C2.528 436.5-.0003 430.4 0 423.1C0 417.6 2.529 411.5 7.03 407L71.03 343C80.4 333.7 95.6 333.7 104.1 343C114.3 352.4 114.3 367.6 104.1 376.1H104.1zM95.1 64H337.9C334.1 71.18 332 79.34 332 87.1C332 116.7 355.3 139.1 384 139.1L481.1 139.1C484.4 157.5 494.9 172.5 509.4 181.9C511.1 184.3 513.1 186.6 515.2 188.8C535.5 209.1 568.5 209.1 588.8 188.8L608 169.5V384C608 419.3 579.3 448 544 448H302.1C305.9 440.8 307.1 432.7 307.1 423.1C307.1 395.3 284.7 371.1 255.1 371.1L158.9 372C155.5 354.5 145.1 339.5 130.6 330.1C128.9 327.7 126.9 325.4 124.8 323.2C104.5 302.9 71.54 302.9 51.23 323.2L31.1 342.5V128C31.1 92.65 60.65 64 95.1 64V64zM95.1 192C131.3 192 159.1 163.3 159.1 128H95.1V192zM544 384V320C508.7 320 480 348.7 480 384H544zM319.1 352C373 352 416 309 416 256C416 202.1 373 160 319.1 160C266.1 160 223.1 202.1 223.1 256C223.1 309 266.1 352 319.1 352z" />
      </svg>
      {options.titleElement}
    </button>
  );

  return (
    <div className="token-page">
      <Head
        title={
          categoryPaths[0]
            ? `${categoryPaths[0]
                .split('-')
                .join(' ')
                .replace(/\b\b\w/g, (c: string) => c.toUpperCase())
                .split('/')
                .join(', ')}`
            : 'crwdunit - Purchase'
        }
        addPostfixTitle
        description="crwdunit"
        link="crwdunit"
        keywords="Purchase crwdunit"
        imageCard=""
        largeTwitterCard
      />
      <img
        src="https://unit.crowdpoint.tech/assets/images/logo-light.png"
        alt=""
        className="block py-4 mx-auto mb-2"
        style={{ objectFit: 'contain', width: '200px' }}
      />
      <div
        className="flex flex-column"
        style={{ maxWidth: '1140px', margin: '0 auto', minHeight: '65vh' }}
      >
        <h2 className="token-page__header text-center mx-auto mb-0 pb-0 max-w-max block">
          A NEW ERA IN PROSPERITY
        </h2>
        <h1 className="text-center mx-auto mt-0 mb-8">
          <span className="crwd-font" style={{ fontSize: '36px' }}>
            crwd<b>units</b>
          </span>{' '}
          Purchase Options
        </h1>
        {!product?.length ? (
          <ProgressSpinner />
        ) : (
          <>
            <TabView className="w-full">
              <TabPanel
                header="Credit/Debit Card"
                aria-label="Credit/Debit Card tab"
                aria-owns="tab"
                headerTemplate={tabHeaderTemplate}
              >
                <DataView
                  rows={perPage}
                  first={first}
                  totalRecords={totalProducts}
                  onPage={e => onPageChange(e)}
                  value={product}
                  layout="grid"
                  itemTemplate={itemTemplate}
                  className="max-w-full col-12 scalein p-0 my-8"
                />
              </TabPanel>
              <TabPanel
                header="ACH"
                aria-label="ACH tab"
                aria-owns="tab"
                headerTemplate={tabHeaderTemplateACH}
              >
                <p>ACH</p>
              </TabPanel>
              <TabPanel
                header="Wire"
                aria-label="Wire tab"
                aria-owns="tab"
                headerTemplate={tabHeaderTemplateWire}
              >
                <p>Wire</p>
              </TabPanel>
            </TabView>
          </>
        )}
      </div>

      {items.length > 0 && (
        <div
          className="categories grid"
          style={{ maxWidth: '1140px', margin: '0 auto' }}
        >
          <CartItemList
            updateCart={updateCart}
            cleanState={() => setUpdateCart(false)}
          />
          <div className="flex flex-column col-12 align-items-end mb-2">
            <p className="text-700 font-light m-0">
              {t('subtotal')}: <span className="text-900">${subTotal}</span>
            </p>
            <p className="text-700 font-light m-0">
              {t('shipping')}: <span className="text-900">{shipping}</span>
            </p>
            <p className="text-700 font-light m-0">
              {t('taxes')}:{' '}
              <span className="text-900">
                {taxes !== '0.00' ? `$${taxes}` : 'Calculated at checkout'}
              </span>
            </p>
            <p className="text-700 font-light mt-2 mb-3 text-2xl">
              {t('total')}: <span className="text-900">${total}</span>
            </p>
            <Button
              className="uppercase border-noround c-button-primary"
              label="Checkout"
              onClick={() => {
                ReactGA.event({
                  category: 'ecommerce',
                  action: 'cart_checkout_button',
                  label: 'Cart Checkout Button',
                });
                setUpdateCart(true);
              }}
            />
          </div>
        </div>
      )}
      <img
        src="https://unit.crowdpoint.tech/assets/images/crwdworld_footer.png"
        alt=""
        className="w-2 pt-8 block mx-auto mb-2 md:mb-0"
        style={{ objectFit: 'contain' }}
      />
      <img
        src="https://unit.crowdpoint.tech/assets/images/crwdworld_plane2.png"
        alt=""
        className="w-full"
        style={{ objectFit: 'contain' }}
      />
      <div
        className="py-4 px-4"
        style={{
          fontSize: '3px !important',
          textAlign: 'justify',
          opacity: '0.65',
        }}
      >
        <p className="py-0 my-0">Disclaimer</p>
        <p>
          Past performance is not indicative of future performance. Any
          financial projections or returns shown are illustrative examples only,
          and there can be no assurance that any valuations provided are
          accurate or in agreement with market or industry valuations. We make
          no representations or warranties as to the accuracy of information and
          accept no liability therefore. There is a potential for loss of part
          or ALL of the investment capital, and each investor should understand
          that all capital invested may be lost. Investors should only consider
          these investments if they have no need for liquidity and can bear the
          risk of losing their entire investment.
        </p>
        <p>
          Neither the Securities and Exchange Commission nor any federal or
          state securities commission or any other regulatory authority has
          recommended or approved of the investment or the accuracy or
          inaccuracy of any of the information or materials provided. Private
          equity investments have significant risks. Investments are restricted
          securities and are subject to restrictions on transfer as described in
          the offering materials. Investment opportunities discussed are
          “private placements” of securities that are not publicly traded, are
          subject to holding period requirements, and are intended for investors
          who do not need a liquid investment.
        </p>
        <p>
          This may contain “forward looking statements” which are not
          guaranteed. All investors should make their own determination of
          whether or not to make any investment, based on their own independent
          evaluation and analysis. It is solely your responsibility to comply
          with the laws and regulations of your country of residence.
        </p>
      </div>
    </div>
  );
}
